<!--
  SearchableSelect — Adaptive searchable select for all apps.
  Desktop (md+): positioned dropdown with search.
  Mobile (<md): centered modal with search + scrollable list.
  Self-contained: no dependency on project-specific Dialog/Modal components.
-->
<template>
  <div ref="rootRef" class="relative">
    <!-- Desktop trigger (md+) -->
    <button
      ref="desktopTriggerRef"
      type="button"
      :disabled="disabled"
      :class="[
        'hidden md:flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm',
        'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer',
        triggerBorderClass,
      ]"
      aria-haspopup="listbox"
      :aria-expanded="desktopOpen"
      @click="toggleDesktop"
    >
      <span class="truncate" :class="hasSelection ? 'text-foreground' : 'text-muted-foreground'">
        {{ selectedLabel }}
      </span>
      <svg
        class="ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform"
        :class="{ 'rotate-180': desktopOpen }"
        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      ><path d="m6 9 6 6 6-6" /></svg>
    </button>

    <!-- Desktop dropdown -->
    <div
      v-if="desktopOpen"
      class="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-border bg-background shadow-lg"
    >
      <div class="border-b border-border px-3 py-2">
        <div class="relative">
          <svg
            class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          ><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          <input
            ref="desktopSearchRef"
            v-model="searchQuery"
            type="text"
            :placeholder="searchPlaceholder"
            class="w-full bg-transparent py-1.5 pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          />
        </div>
      </div>
      <div class="max-h-64 overflow-y-auto overscroll-contain" role="listbox">
        <button
          v-for="opt in filteredOptions"
          :key="opt.value"
          type="button"
          class="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer hover:bg-accent"
          :class="{
            'bg-primary/10': modelValue === opt.value,
            'opacity-50 cursor-not-allowed': opt.disabled,
          }"
          :aria-selected="modelValue === opt.value"
          :disabled="opt.disabled"
          @click="selectOption(opt.value)"
        >
          <span class="truncate text-sm text-foreground">{{ opt.label }}</span>
          <svg
            v-if="modelValue === opt.value"
            class="h-4 w-4 shrink-0 text-primary"
            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          ><path d="M20 6 9 17l-5-5" /></svg>
        </button>
        <p v-if="filteredOptions.length === 0" class="px-4 py-3 text-center text-sm text-muted-foreground">
          {{ noResultsText }}
        </p>
      </div>
    </div>

    <!-- Mobile trigger (<md) -->
    <button
      type="button"
      :disabled="disabled"
      :class="[
        'flex md:hidden h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm',
        'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer',
        triggerBorderClass,
      ]"
      aria-haspopup="dialog"
      :aria-expanded="mobileOpen"
      @click="openMobile"
    >
      <span class="truncate" :class="hasSelection ? 'text-foreground' : 'text-muted-foreground'">
        {{ selectedLabel }}
      </span>
      <svg
        class="ml-2 h-4 w-4 shrink-0 opacity-50"
        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      ><path d="m6 9 6 6 6-6" /></svg>
    </button>

    <!-- Mobile modal -->
    <Teleport to="body">
      <Transition name="ss-modal">
        <div
          v-if="mobileOpen"
          class="fixed inset-0 z-70 flex items-center justify-center p-4 md:hidden"
        >
          <div class="fixed inset-0 bg-black/40" @click="closeMobile" />
          <div class="relative z-10 flex max-h-[80vh] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl bg-background shadow-lg">
            <!-- Header -->
            <div class="flex items-center justify-between border-b border-border px-5 pt-5 pb-3">
              <h2 class="text-lg font-semibold text-foreground">{{ mobileTitle || placeholder }}</h2>
              <button
                type="button"
                class="rounded-full p-1.5 opacity-70 transition-opacity cursor-pointer hover:opacity-100"
                @click="closeMobile"
              >
                <svg
                  class="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                ><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
              </button>
            </div>
            <!-- Search -->
            <div class="px-4 py-3">
              <div class="relative">
                <svg
                  class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                ><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                <input
                  ref="mobileSearchRef"
                  v-model="searchQuery"
                  type="text"
                  :placeholder="searchPlaceholder"
                  class="w-full rounded-xl border border-border bg-muted/50 py-2.5 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/10"
                />
              </div>
            </div>
            <!-- Options -->
            <div class="flex-1 overflow-y-auto overscroll-contain border-t border-border" role="listbox">
              <button
                v-for="opt in filteredOptions"
                :key="opt.value"
                type="button"
                class="w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors cursor-pointer hover:bg-accent"
                :class="{
                  'bg-primary/10': modelValue === opt.value,
                  'opacity-50 cursor-not-allowed': opt.disabled,
                }"
                :aria-selected="modelValue === opt.value"
                :disabled="opt.disabled"
                @click="selectOption(opt.value, true)"
              >
                <span class="truncate text-base text-foreground">{{ opt.label }}</span>
                <svg
                  v-if="modelValue === opt.value"
                  class="h-[18px] w-[18px] shrink-0 text-primary"
                  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                ><path d="M20 6 9 17l-5-5" /></svg>
              </button>
              <p v-if="filteredOptions.length === 0" class="px-5 py-6 text-center text-sm text-muted-foreground">
                {{ noResultsText }}
              </p>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

export interface SearchableSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<{
  modelValue?: string;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  mobileTitle?: string;
  noResultsText?: string;
  error?: boolean;
}>(), {
  modelValue: '',
  placeholder: 'Select...',
  searchPlaceholder: 'Search...',
  disabled: false,
  noResultsText: 'No results found',
  error: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  open: [];
}>();

const rootRef = ref<HTMLElement | null>(null);
const desktopTriggerRef = ref<HTMLElement | null>(null);
const desktopSearchRef = ref<HTMLInputElement | null>(null);
const mobileSearchRef = ref<HTMLInputElement | null>(null);

const desktopOpen = ref(false);
const mobileOpen = ref(false);
const searchQuery = ref('');

const triggerBorderClass = computed(() =>
  props.error ? 'border-destructive' : 'border-input',
);

const hasSelection = computed(() =>
  props.options.some(o => o.value === props.modelValue),
);

const selectedLabel = computed(() => {
  const opt = props.options.find(o => o.value === props.modelValue);
  return opt ? opt.label : (props.placeholder ?? '');
});

const filteredOptions = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return props.options;
  return props.options.filter(opt => opt.label.toLowerCase().includes(q));
});

function toggleDesktop() {
  desktopOpen.value = !desktopOpen.value;
  if (desktopOpen.value) {
    emit('open');
    nextTick(() => desktopSearchRef.value?.focus());
  } else {
    searchQuery.value = '';
  }
}

function openMobile() {
  mobileOpen.value = true;
  lockBodyScroll();
  emit('open');
}

function closeMobile() {
  mobileOpen.value = false;
  searchQuery.value = '';
  unlockBodyScroll();
}

function selectOption(value: string, fromMobile = false) {
  emit('update:modelValue', value);
  if (fromMobile) {
    closeMobile();
  } else {
    desktopOpen.value = false;
    searchQuery.value = '';
    desktopTriggerRef.value?.focus();
  }
}

function handleClickOutside(event: MouseEvent) {
  if (!rootRef.value || !rootRef.value.contains(event.target as Node)) {
    if (desktopOpen.value) {
      desktopOpen.value = false;
      searchQuery.value = '';
    }
  }
}

function handleEscapeKey(event: KeyboardEvent) {
  if (event.key !== 'Escape') return;
  if (desktopOpen.value) {
    desktopOpen.value = false;
    searchQuery.value = '';
    desktopTriggerRef.value?.focus();
  }
  if (mobileOpen.value) {
    closeMobile();
  }
}

// Body scroll locking — shares counter with Dialog.vue
const BODY_LOCK_COUNT_KEY = '__bubbaDialogBodyLockCount';
const BODY_LOCK_OVERFLOW_KEY = '__bubbaDialogBodyLockOverflow';
let isLockedByThis = false;

function lockBodyScroll() {
  if (typeof document === 'undefined' || isLockedByThis) return;
  const w = window as any;
  const count: number = w[BODY_LOCK_COUNT_KEY] ?? 0;
  if (count === 0) {
    w[BODY_LOCK_OVERFLOW_KEY] = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  w[BODY_LOCK_COUNT_KEY] = count + 1;
  isLockedByThis = true;
}

function unlockBodyScroll() {
  if (typeof document === 'undefined' || !isLockedByThis) return;
  const w = window as any;
  const count: number = w[BODY_LOCK_COUNT_KEY] ?? 0;
  const next = Math.max(0, count - 1);
  if (next === 0) {
    document.body.style.overflow = w[BODY_LOCK_OVERFLOW_KEY] ?? '';
    delete w[BODY_LOCK_OVERFLOW_KEY];
    delete w[BODY_LOCK_COUNT_KEY];
  } else {
    w[BODY_LOCK_COUNT_KEY] = next;
  }
  isLockedByThis = false;
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
  document.addEventListener('keydown', handleEscapeKey);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  document.removeEventListener('keydown', handleEscapeKey);
  unlockBodyScroll();
});
</script>

<style scoped>
.ss-modal-enter-active,
.ss-modal-leave-active {
  transition: opacity 0.2s ease;
}

.ss-modal-enter-active > .relative,
.ss-modal-leave-active > .relative {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.ss-modal-enter-from,
.ss-modal-leave-to {
  opacity: 0;
}

.ss-modal-enter-from > .relative,
.ss-modal-leave-to > .relative {
  transform: scale(0.95);
  opacity: 0;
}
</style>
